import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import React, { forwardRef, useState } from 'react';

import useFormatMessage from '@/hooks/useFormatMessage';

export const searchLabelProps = shrink => ({
  shrink,
  sx: {
    '&:not(.MuiInputLabel-shrink)': {
      transform: 'translate(41px, 16px) scale(1)',
      maxWidth: 'calc(100% - 60px)',
    },
  },
});

const SearchInput = forwardRef(function SearchInput(
  {
    value,
    onChange,
    onFocus,
    onClear,
    onKeyDown,
    listboxId,
    activeOptionId,
    expanded,
    triggerMode,
    onTrigger,
  },
  ref
) {
  const formatMessage = useFormatMessage();
  const [focused, setFocused] = useState(false);

  const handleTriggerKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onTrigger?.(e);
    }
  };

  return (
    <TextField
      inputRef={triggerMode ? undefined : ref}
      value={value}
      onChange={e => onChange(e.target.value)}
      onFocus={e => {
        setFocused(true);
        if (!triggerMode) onFocus(e);
      }}
      onBlur={() => setFocused(false)}
      onKeyDown={triggerMode ? handleTriggerKeyDown : onKeyDown}
      onClick={triggerMode ? onTrigger : undefined}
      label={formatMessage('rental_request_flow_search_label')}
      InputLabelProps={searchLabelProps(focused || Boolean(value))}
      variant="outlined"
      fullWidth
      autoComplete="off"
      sx={
        triggerMode
          ? { '& input': { caretColor: 'transparent', cursor: 'pointer' } }
          : undefined
      }
      InputProps={{
        'data-test-subject': 'rental-request-flow-input',
        readOnly: triggerMode,
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon color="primary" />
          </InputAdornment>
        ),
        endAdornment: value ? (
          <IconButton
            aria-label={formatMessage('rental_request_flow_clear_aria')}
            onClick={e => {
              e.stopPropagation();
              onClear(e);
            }}
            edge="end"
            size="small"
            sx={{ minWidth: 44, minHeight: 44 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : null,
        ...(triggerMode
          ? {
              role: 'button',
              'aria-haspopup': 'dialog',
            }
          : {
              role: 'combobox',
              'aria-expanded': expanded,
              'aria-controls': listboxId,
              'aria-autocomplete': 'list',
              'aria-activedescendant': activeOptionId || undefined,
            }),
      }}
    />
  );
});

SearchInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
  listboxId: PropTypes.string.isRequired,
  activeOptionId: PropTypes.string,
  expanded: PropTypes.bool.isRequired,
  triggerMode: PropTypes.bool,
  onTrigger: PropTypes.func,
};

export default SearchInput;
